if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      resetPersonalizationState() {
        this.querySelectorAll('[data-personalization-fields]').forEach((fieldsWrap) => {
          fieldsWrap.classList.remove('is-open');
          fieldsWrap.classList.add('is-hidden');
          fieldsWrap.setAttribute('aria-hidden', 'true');
          fieldsWrap.style.display = 'none';
        });
      }

      restoreDocumentOverflow() {
        const hasOpenQuickAdd = document.querySelector('quick-add-modal[open], .quick-add-modal[open]');
        if (hasOpenQuickAdd) return;

        document.documentElement.classList.remove('overflow-hidden', 'product-personalization-lock');
        document.body.classList.remove('overflow-hidden', 'product-personalization-lock');

        if (document.documentElement.style.overflow === 'hidden') document.documentElement.style.overflow = '';
        if (document.body.style.overflow === 'hidden') document.body.style.overflow = '';
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.resetPersonalizationState();
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
        window.requestAnimationFrame(() => this.restoreDocumentOverflow());
        window.setTimeout(() => this.restoreDocumentOverflow(), 120);
        window.setTimeout(() => this.restoreDocumentOverflow(), 320);
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();
            this.initPersonalization();

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }

      initPersonalization() {
        const productInfo = this.modalContent.querySelector('product-info');
        const fieldsWrap = this.modalContent.querySelector('[data-personalization-fields]');
        if (!productInfo || !fieldsWrap || fieldsWrap.dataset.quickAddPersonalizationReady === 'true') return;

        fieldsWrap.dataset.quickAddPersonalizationReady = 'true';

        const normalize = (value) =>
          String(value || '')
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .trim();

        const isPositive = (value) => {
          const text = normalize(value);
          if (!text) return false;
          if (['yes', 'sim', 'customize', 'personalizar', 'com personalizacao'].includes(text)) return true;
          if (text.includes('with name') && !text.includes('no name')) return true;
          if (text.includes('name & number') && !text.includes('no name')) return true;
          if (text.includes('name/number') && !text.includes('no name')) return true;
          if (text.includes('customize') && !text.includes('not') && !text.startsWith('no')) return true;
          if (text.includes('personaliz') && !text.includes('sem') && !text.includes('nao')) return true;
          return false;
        };

        const setFieldsEnabled = (enabled) => {
          fieldsWrap.querySelectorAll('input, textarea, select').forEach((field) => {
            if (field.matches('[type="hidden"], script, [data-personalization-variants], .patch-checkbox-input')) return;

            if (!field.dataset.personalizationOriginalRequired) {
              field.dataset.personalizationOriginalRequired = field.required ? 'true' : 'false';
            }

            field.disabled = !enabled;
            field.required = enabled && field.dataset.personalizationOriginalRequired === 'true';
          });
        };

        const selectedPositiveInput = () => {
          const checkedRadio = Array.from(productInfo.querySelectorAll('input[type="radio"]:checked')).find((input) =>
            isPositive(input.value)
          );
          if (checkedRadio) return checkedRadio;

          const selectedOption = Array.from(productInfo.querySelectorAll('select option:checked')).find((option) =>
            isPositive(option.value || option.textContent)
          );
          return selectedOption || null;
        };

        const openFields = () => {
          fieldsWrap.style.display = '';
          fieldsWrap.classList.add('is-open');
          fieldsWrap.classList.remove('is-hidden');
          fieldsWrap.setAttribute('aria-hidden', 'false');
          document.documentElement.classList.add('overflow-hidden', 'product-personalization-lock');
          document.body.classList.add('overflow-hidden', 'product-personalization-lock');
        };

        const closeFields = () => {
          fieldsWrap.classList.remove('is-open');
          fieldsWrap.classList.add('is-hidden');
          fieldsWrap.setAttribute('aria-hidden', 'true');

          window.setTimeout(() => {
            if (!fieldsWrap.classList.contains('is-open')) fieldsWrap.style.display = 'none';
          }, 250);

          this.restoreDocumentOverflow();
        };

        const switchToNoOption = () => {
          const radios = Array.from(productInfo.querySelectorAll('input[type="radio"]'));
          const positiveRadio =
            radios.find((radio) => radio.checked && isPositive(radio.value)) || radios.find((radio) => isPositive(radio.value));
          if (!positiveRadio) return;

          const noRadio = radios.find((radio) => radio.name === positiveRadio.name && !isPositive(radio.value));
          if (!noRadio || noRadio.checked) return;

          noRadio.checked = true;
          noRadio.dispatchEvent(new Event('change', { bubbles: true }));
          noRadio.dispatchEvent(new Event('input', { bubbles: true }));
        };

        const sync = () => {
          const shouldShow = Boolean(selectedPositiveInput());
          setFieldsEnabled(shouldShow);

          if (shouldShow) {
            openFields();
          } else {
            closeFields();
          }
        };

        fieldsWrap.querySelectorAll('[data-personalization-close]').forEach((button) => {
          button.addEventListener('click', () => {
            closeFields();
            window.setTimeout(switchToNoOption, 50);
          });
        });

        productInfo.addEventListener('change', (event) => {
          if (!event.target.matches('input[type="radio"], select')) return;
          window.setTimeout(sync, 0);
        });

        window.setTimeout(sync, 0);
        window.setTimeout(sync, 200);
      }
    }
  );
}
